import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../Properties/Lettings.css";
import Footer from "../Components/Navigation/Footer";
import Navigation from "../Components/Navigation/Navigation";
import PropertiesList from "../Properties/PropertiesList";
import "./Lettings.css";
import Banner from "../Components/Banner";
import BrownBuilding from "../Assets/Images/AboutUs/brown-building.jpeg";

function Lettings() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLetAgreed, setShowLetAgreed] = useState(true);

  const [townPostcodeCounty, setTownPostcodeCounty] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minBedrooms, setMinBedrooms] = useState("");
  const [maxBedrooms, setMaxBedrooms] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const searchBarRef = useRef(null);
  const filterBarRef = useRef(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const pageSize = 21; // Number of properties per page

  useEffect(() => {
    document.title = "Lettings | Zen Homes";
    fetchAllProperties(currentPage);
  }, [currentPage]); // Fetch properties when page changes

  useEffect(() => {
    if (properties.length > 0) {
      applyFilters(properties, showLetAgreed, currentPage, 21);
    }
  }, [currentPage, properties, showLetAgreed]);

  const fetchAllProperties = async () => {
    let allProperties = [];
    let nextPageUrl = `https://webapi.10ninety.co.uk/properties/?trans_type_id=2&page_size=100`;

    setLoading(true);

    try {
      while (nextPageUrl) {
        const response = await fetch(nextPageUrl, {
          method: "GET",
          headers: {
            "10ninety-webapi-key": process.env.REACT_APP_API_KEY,
            Accept: "application/json",
          },
        });

        if (!response.ok) throw new Error(`Error: ${response.status}`);

        const data = await response.json();
        allProperties = [...allProperties, ...data.properties];

        nextPageUrl = data.paging?.next || null;
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setProperties(allProperties);
      applyFilters(allProperties, showLetAgreed, 1, 21);
      setLoading(false);
    }
  };

  const applyFilters = (data, showLetAgreedState, page = 1, pageSize = 21) => {
    let filtered = data.filter((property) => {
      const isAvailable = property.status_id === 0;
      const isLetAgreed = property.status_id === 5 || property.status_id === 7;

      // Improved Location Filtering
      let matchesSearch = true;
      if (townPostcodeCounty) {
        let searchTerm = townPostcodeCounty.toLowerCase().trim();

        let postcodePrefix = property.postcode_1
          ? property.postcode_1.toLowerCase()
          : "";
        let county = property.county ? property.county.toLowerCase() : "";
        let town = property.address_2 ? property.address_2.toLowerCase() : "";

        // Use regex to match exact postcode
        let regex = new RegExp(`\\b${searchTerm}\\b`);

        matchesSearch =
          regex.test(postcodePrefix) ||
          county.includes(searchTerm) ||
          town.includes(searchTerm);
      }

      // Price Filtering
      let matchesPrice =
        (!minPrice || property.price >= parseInt(minPrice, 10)) &&
        (!maxPrice || property.price <= parseInt(maxPrice, 10));

      // Bedrooms Filtering
      let minBed = minBedrooms ? parseInt(minBedrooms, 10) : null;
      let maxBed = maxBedrooms ? parseInt(maxBedrooms, 10) : null;
      let matchesBedrooms =
        (minBed === null || property.bedrooms >= minBed) &&
        (maxBed === null || property.bedrooms <= maxBed);

      return (
        (showLetAgreedState ? isAvailable || isLetAgreed : isAvailable) &&
        matchesSearch &&
        matchesPrice &&
        matchesBedrooms
      );
    });

    // Sort available properties first
    filtered.sort((a, b) => {
      if (a.status_id === 0 && b.status_id !== 0) return -1;
      if (a.status_id !== 0 && b.status_id === 0) return 1;
      return 0;
    });

    const startIndex = (page - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + pageSize);

    setFilteredProperties(paginated);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleToggle = () => {
    setShowLetAgreed((prev) => {
      const newShowLetAgreed = !prev;
      setCurrentPage(1);
      setTimeout(
        () => applyFilters(properties, newShowLetAgreed, 1, pageSize),
        0
      );
      return newShowLetAgreed;
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    applyFilters(properties, showLetAgreed, 1, pageSize);
  };

  const toggleExpand = () => {
    setIsExpanded(true);
  };

  const resetFilters = () => {
    setTownPostcodeCounty("");
    setMinPrice("");
    setMaxPrice("");
    setMinBedrooms("");
    setMaxBedrooms("");
    setShowLetAgreed(true); // Reset to default if needed
    setCurrentPage(1);
    setFilteredProperties(properties);
  };

  return (
    <div className="lettingsContainers">
      <Navigation
        linkColor="black"
        submenuColor="black"
        menuIconColor="black"
      />
      <Banner title="Renting with Zen Homes" backgroundImage={BrownBuilding} />

      {/* Search Bar */}
      <div className="searchbarContainer">
        <form className="searchbar" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            className={`searchInput ${isExpanded ? "expanded" : ""}`}
            ref={searchBarRef}
            onClick={toggleExpand}
            placeholder="Search location e.g. N9 or Essex"
            aria-label="Location Search"
            value={townPostcodeCounty}
            onChange={(e) => setTownPostcodeCounty(e.target.value)}
          />
          <button type="submit" className="searchButton">
            Search
          </button>
          <button type="button" className="resetButton" onClick={resetFilters}>
            Reset Filters
          </button>
        </form>
        {isExpanded && (
          <div className="filterBar" ref={filterBarRef}>
            <div className={`searchGroup ${minPrice ? "hasValue" : ""}`}>
              {minPrice && <label className="searchLabel">Min Price</label>}
              <select
                className="searchSelect"
                aria-label="Min Price"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              >
                <option value="">Min Price</option>
                {[
                  500, 750, 1000, 1250, 1500, 2000, 2500, 3000, 3500, 4000,
                  5000, 7500, 10000, 50000,
                ].map((price) => (
                  <option key={price} value={price}>
                    £{price.toLocaleString()}pcm
                  </option>
                ))}
              </select>
            </div>
            <div className={`searchGroup ${maxPrice ? "hasValue" : ""}`}>
              {maxPrice && <label className="searchLabel">Max Price</label>}
              <select
                className="searchSelect"
                aria-label="Max Price"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              >
                <option value="">Max Price</option>
                {[
                  500, 750, 1000, 1250, 1500, 2000, 2500, 3000, 3500, 4000,
                  5000, 7500, 10000, 50000,
                ].map((price) => (
                  <option key={price} value={price}>
                    £{price.toLocaleString()}pcm
                  </option>
                ))}
              </select>
            </div>
            <div className={`searchGroup ${minBedrooms ? "hasValue" : ""}`}>
              {minBedrooms && (
                <label className="searchLabel">Min Bedrooms</label>
              )}
              <select
                className="searchSelect"
                aria-label="Min Bedroom"
                value={minBedrooms}
                onChange={(e) => setMinBedrooms(e.target.value)}
              >
                <option value="">Min Bedroom</option>
                {[0, 1, 2, 3, 4, 5].map((bed) => (
                  <option key={bed} value={bed}>
                    {bed}
                  </option>
                ))}
              </select>
            </div>
            <div className={`searchGroup ${maxBedrooms ? "hasValue" : ""}`}>
              {maxBedrooms && (
                <label className="searchLabel">Max Bedrooms</label>
              )}
              <select
                className="searchSelect"
                aria-label="Max Bedroom"
                value={maxBedrooms}
                onChange={(e) => setMaxBedrooms(e.target.value)}
              >
                <option value="">Max Bedroom</option>
                {[0, 1, 2, 3, 4, 5].map((bed) => (
                  <option key={bed} value={bed}>
                    {bed}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Filters & Toggle */}
      <div className="toggleBar">
        <label>
          <input
            type="checkbox"
            checked={showLetAgreed}
            onChange={handleToggle}
          />
          Show Let Agreed / Let Properties
        </label>
      </div>

      {/* Property Listings */}
      {loading && <p>Loading...</p>}
      {!loading && filteredProperties.length === 0 && (
        <p>No properties found.</p>
      )}
      {!loading && filteredProperties.length > 0 && (
        <div className="propertiesList">
          {filteredProperties.map((property) => (
            <PropertiesList key={property.property_ref} {...property} />
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index + 1}
            onClick={() => setCurrentPage(index + 1)}
            className={currentPage === index + 1 ? "active" : ""}
          >
            {index + 1}
          </button>
        ))}
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Lettings;
